import React, { useState } from 'react';
import PropTypes, { number } from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { Button } from "@material-ui/core";
import { useMutation } from "react-apollo";
import { TextField, Checkbox, FormControlLabel, Select, MenuItem, InputLabel, FormControl, FormGroup, Switch, Typography, Box } from "@material-ui/core";
import { withSnackbar } from 'notistack';
import { gql } from "graphql-tag";
import { regionForCountry } from '../../lib/findBobUtils';
import { get } from 'lodash';


export const UPDATE_PARTIAL_SALE = gql`
  mutation UpdatePartialSale($id: ID!, 
                              $segment_details: String,
                              $description: String!, 
                              $number_of_clients: Int!,
                              $assets_under_management: Float!,
                              $fee_based_revenue: Float!,
                              $commission_revenue: Float!,
                              $trailer_revenue: Float!,
                              $direct_revenue: Float!,
                              $asking_price: Float!,
                              $states: [String!],
                              $questions: [String!],
                              $answers: [Boolean!],
                              $client_demographics_details: String,
                              $services_provided_details: String,
                              $other_details: String) {
    updatePartialSale(  id: $id, 
                        segment_details: $segment_details,
                        description: $description,
                        number_of_clients: $number_of_clients,
                        assets_under_management: $assets_under_management,
                        fee_based_revenue: $fee_based_revenue,
                        commission_revenue: $commission_revenue,
                        trailer_revenue: $trailer_revenue,
                        direct_revenue: $direct_revenue,
                        asking_price: $asking_price,
                        states: $states,
                        questions: $questions,
                        answers: $answers,
                        client_demographics_details: $client_demographics_details,
                        services_provided_details: $services_provided_details,
                        other_details: $other_details ) {
      id
      segment_details
      description
      number_of_clients
      assets_under_management
      fee_based_revenue
      asking_price
      commission_revenue
      trails_revenue
      direct_revenue
      states
      questions
      answers
      client_demographics_details
      services_provided_details
      other_details
    }

  }
`;

const useRowStyles = makeStyles({
  root: {
    '& > *': {
      borderBottom: 'unset',
    },
  },
});

const PartialSaleListing = (props) => {
  const [partialSale, updateLocalPartialSale] = React.useState(props.partialSale);
  const [open, setOpen] = React.useState(false);
  const [updatePartialSale, { data, loading, error }] = useMutation(UPDATE_PARTIAL_SALE);
  const { setPartialSale, enqueueSnackbar, company } = props;
  const classes = useRowStyles();
  const numberOfInterests = partialSale.partial_sale_listing_users.length;
  const [isNegotiable, setIsNegotiable] = useState(
    !partialSale.asking_price || parseFloat(partialSale.asking_price) === 0
  );
  const [isValid, setIsValid] = useState(true);

  const handleNumberOfClientsChange = (e) => {
    const value = e.target.value;
    updateLocalPartialSale({ ...partialSale, number_of_clients: value });
    setIsValid(value > 0);
  };


  const updateAskingPrice = (e) => {
    const newPrice = Number(e.target.value);
    updateLocalPartialSale({ ...partialSale, asking_price: newPrice });
    // Update negotiable checkbox based on price value
    if (!newPrice || newPrice === 0) {
      setIsNegotiable(true);
    } else {
      setIsNegotiable(false);
    }
  };

  const handleNegotiableChange = (e) => {
    const checked = e.target.checked;
    setIsNegotiable(checked);
    if (checked) {
      updateLocalPartialSale({ ...partialSale, asking_price: 0 });
    }
  };

  const partialSalesPropertyCasualty = company?.feature_types?.includes('partial_sales_property_casualty') || false;
  const hideProductSalesPurchasePrice = company?.feature_types?.includes('hide_partial_sales_purchase_price') || false;
  const partialSalesDetailedQuestions = company?.feature_types?.includes('partial_sales_detailed_questions') || false;

  const handleCheckChange = (event) => {
    const index = parseInt(event.target.name);
    const newAnswers = [...(partialSale.answers || [])];
    newAnswers[index] = event.target.checked;
    updateLocalPartialSale({ ...partialSale, answers: newAnswers });
  };

  const handleMultiSelectChange = (event) => {
    updateLocalPartialSale({ ...partialSale, states: event.target.value });
  };

  return (
    <React.Fragment>
      <TextField
        id="segment_details"
        label="Segment Name"
        value={partialSale.segment_details}
        onChange={(e) => {
          if (e.target.value.length <= 100) {
            updateLocalPartialSale({ ...partialSale, segment_details: e.target.value });
          }
        }}
        fullWidth
        helperText={`${(partialSale.segment_details || '').length}/100 characters`}
      />
      <br />
      <br />
      <TextField
        id="description"
        label="Description"
        multiline
        numberOfLines={4}
        limit={250}
        value={partialSale.description}
        onChange={(e) => updateLocalPartialSale({ ...partialSale, description: e.target.value })}
        fullWidth
      />
      <br />
      <br />
      
      {/* States/Provinces */}
      <FormControl fullWidth margin="normal">
        <InputLabel>{company.country === 'ca' ? "Provinces" : "States"}</InputLabel>
        <Select
          multiple
          value={partialSale.states || []}
          onChange={handleMultiSelectChange}
          renderValue={(selected) => selected.join(', ')}
        >
          {regionForCountry(company.country).map((state) => (
            <MenuItem key={state} value={state}>
              {state}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <br />
      <br />
      
      {/* Questions */}
      {partialSale.questions && partialSale.questions.length > 0 && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Questions
          </Typography>
          <FormGroup>
            {partialSale.questions.map((question, index) => (
              <FormControlLabel
                key={index}
                control={
                  <Switch
                    checked={get(partialSale, `[answers][${index}]`, false)}
                    onChange={handleCheckChange}
                    name={String(index)}
                  />
                }
                label={question}
              />
            ))}
          </FormGroup>
          <br />
        </Box>
      )}
      
      {/* Detailed Questions (if feature flag is enabled) */}
      {partialSalesDetailedQuestions && (
        <>
          <Box mt={2}>
            <Typography variant="body2" gutterBottom>
              Client Demographics:
            </Typography>
            <TextField
              name="client_demographics_details"
              value={partialSale.client_demographics_details || ''}
              placeholder="Briefly describe the client demographics, e.g., high-net-worth individuals, small businesses, etc."
              onChange={(e) => {
                if (e.target.value.length <= 250) {
                  updateLocalPartialSale({ ...partialSale, client_demographics_details: e.target.value });
                }
              }}
              multiline
              rows={4}
              variant="outlined"
              fullWidth
              helperText={`${(partialSale.client_demographics_details || '').length}/250`}
            />
          </Box>
          <Box mt={2}>
            <Typography variant="body2" gutterBottom>
              Services Provided:
            </Typography>
            <TextField
              name="services_provided_details"
              value={partialSale.services_provided_details || ''}
              placeholder="List the services provided to these clients, e.g., wealth management, retirement planning, investment advisory, etc."
              onChange={(e) => {
                if (e.target.value.length <= 250) {
                  updateLocalPartialSale({ ...partialSale, services_provided_details: e.target.value });
                }
              }}
              multiline
              rows={4}
              variant="outlined"
              fullWidth
              helperText={`${(partialSale.services_provided_details || '').length}/250`}
            />
          </Box>
          <Box mt={2}>
            <Typography variant="body2" gutterBottom>
              Other:
            </Typography>
            <TextField
              name="other_details"
              value={partialSale.other_details || ''}
              placeholder="e.g., These clients have been meticulously nurtured and serviced, and we are seeking a buyer who can continue to provide exceptional financial guidance and support."
              onChange={(e) => {
                if (e.target.value.length <= 250) {
                  updateLocalPartialSale({ ...partialSale, other_details: e.target.value });
                }
              }}
              multiline
              rows={4}
              variant="outlined"
              fullWidth
              helperText={`${(partialSale.other_details || '').length}/250`}
            />
          </Box>
          <br />
        </>
      )}
      
      <TextField
        id="number_of_clients"
        label="Number Of Clients"
        value={partialSale.number_of_clients}
        onChange={handleNumberOfClientsChange}
        type="number"
      />
      <br />
      <br />
      <TextField
        id="assets_under_management"
        label={partialSalesPropertyCasualty ? "Total Premium" : "Assets Under Management"}
        value={partialSale.assets_under_management}
        onChange={(e) => updateLocalPartialSale({ ...partialSale, assets_under_management: e.target.value })}
        type="number"
      />
      <br />
      <br />
      <TextField
        id="fee_based_revenue"
        label={partialSalesPropertyCasualty ? "Recurring Commission Revenue" : "Fee Based Revenue"}
        value={partialSale.fee_based_revenue}
        onChange={(e) => updateLocalPartialSale({ ...partialSale, fee_based_revenue: e.target.value })}
        type="number"
      />
      <br />
      <br />
      <TextField
        id="commission_revenue"
        label={partialSalesPropertyCasualty ? "New Commission Revenue" : "Commission Revenue"}
        value={partialSale.commission_revenue}
        onChange={(e) => updateLocalPartialSale({ ...partialSale, commission_revenue: e.target.value })}
        type="number"
      />
      <br />
      <br />
      { !partialSalesPropertyCasualty && (
        <div>
          <TextField
          id="trailer_revenue"
          label="Trailer Revenue"
          value={partialSale.trails_revenue}
          onChange={(e) => updateLocalPartialSale({ ...partialSale, trails_revenue: e.target.value })}
          type="number"
          />
          <br />
          <br />
          <TextField
            id="direct_revenue"
            label="Direct Revenue"
            value={partialSale.direct_revenue}
            onChange={(e) => updateLocalPartialSale({ ...partialSale, direct_revenue: e.target.value })}
            type="number"
          />
          <br />
          <br />
        </div>
      )}
      {!hideProductSalesPurchasePrice && (
        <div>
          <TextField
            id="asking_price"
            label="Asking Price"
            value={partialSale.asking_price}
            onChange={updateAskingPrice}
            type="number"
          />
          <br />
          <FormControlLabel
            control={
              <Checkbox
                checked={isNegotiable}
                onChange={handleNegotiableChange}
                name="isNegotiable"
                color="primary"
              />
            }
            label="Price is negotiable"
          />
        </div>
      )}
      <br />
      <br />
      <Button variant="contained" color="primary" onClick={() => {
        const { id, segment_details, description, number_of_clients, assets_under_management, fee_based_revenue, asking_price, states, questions, answers, client_demographics_details, services_provided_details, other_details } = partialSale;
        updatePartialSale({ 
          variables: { 
            id, 
            segment_details: segment_details || null,
            description,
            number_of_clients: parseInt(number_of_clients),
            assets_under_management: parseFloat(assets_under_management),
            fee_based_revenue: parseFloat(fee_based_revenue),
            commission_revenue: parseFloat(partialSale.commission_revenue),
            trailer_revenue: parseFloat(partialSale.trails_revenue),
            direct_revenue: parseFloat(partialSale.direct_revenue),
            asking_price: parseFloat(asking_price),
            states: states || [],
            questions: questions || [],
            answers: answers || [],
            client_demographics_details: client_demographics_details || null,
            services_provided_details: services_provided_details || null,
            other_details: other_details || null
          } 
        }).then(result => {

          // Update parent component with the saved data from the mutation response
          if (result.data && result.data.updatePartialSale) {
            setPartialSale({ ...partialSale, ...result.data.updatePartialSale });
          }
          // Write a success message to the user via snackbar
          enqueueSnackbar("Listing Updated", {
            variant: 'success',
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center',
            }
          });

        }).catch(error => {
          console.error('Error updating partial sale:', error);
          enqueueSnackbar("Failed to update listing", {
            variant: 'error',
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center',
            }
          });
        })
      }}
      disabled={!isValid}> 
      Save </Button>
      <br />
      <br />
    </React.Fragment>
  );
}

PartialSaleListing.propTypes = {
  row: PropTypes.shape({
    calories: PropTypes.number.isRequired,
    carbs: PropTypes.number.isRequired,
    fat: PropTypes.number.isRequired,
    history: PropTypes.arrayOf(
      PropTypes.shape({
        amount: PropTypes.number.isRequired,
        customerId: PropTypes.string.isRequired,
        date: PropTypes.string.isRequired,
      }),
    ).isRequired,
    name: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    protein: PropTypes.number.isRequired,
  }).isRequired,
  company: PropTypes.shape({
    feature_types: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
};

export default withSnackbar(PartialSaleListing);